import React, { useState, useEffect } from 'react';
import GameTabs from './GameTabs';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import { useAuth } from '../../context/AuthContext';
import { gamesApi } from '../../api/games';
import getGameType from '../../utils/games';
import getGameImage from '../../utils/getGameImage';
import LoadingPng from '../../assets/images/Loading.png';
import { Link } from 'react-router-dom';

export default function PlayGame() {
  const [tabsData, setTabsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { tokens, user } = useAuth();
  const [hasGames, setHasGames] = useState(true);

  // Helper function to format game price, matching the one in GameCategories
  const formatGamePrice = (winRate, commissionRate) => {
    if (user.roleId !== 9) {
      return `₱${winRate} | ${commissionRate}%`;
    } else {
      return `₱${winRate}`;
    }
  };

  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      try {
        // Get games from API - exactly like in GameCategories.jsx
        const allGames = await gamesApi.getAllGames(tokens.access.token);
        console.log('allGames', allGames);

        const imageMap = allGames.data.reduce((acc, game) => {
          acc[game.id] = game?.otherSettings?.image || '';
          return acc;
        }, {});

        // Fetch user games
        const userGames = await gamesApi.getUserGames(tokens.access.token);

        if (!userGames || userGames.length === 0) {
          console.log('No user games found');
          setHasGames(false);
          setLoading(false);
          return;
        }

        setHasGames(true);

        // Group userGames by game_settings_id
        const grouped = userGames.reduce((acc, game) => {
          if (!acc[game.game_settings_id]) acc[game.game_settings_id] = [];
          acc[game.game_settings_id].push(game);
          return acc;
        }, {});

        // Build tabsData
        const tabsData = Object.entries(grouped).map(([game_settings_id, games]) => {
          const gameInfo = allGames.data.find(g => g.id === Number(game_settings_id));
          const image = gameInfo?.otherSettings?.image || '';
          const gameType = getGameType(image);
          const GameIcon = getGameImage(gameType);

          return {
            title: gameInfo?.name || `Game ${game_settings_id}`,
            cards: games.map(game => ({
              id: game.id,
              icon: GameIcon,
              title: game.name || gameInfo?.name || `Game ${game.id}`,
              doubleWin: game.doubleWin || game.winRate || '0',
              tripleWin: game.tripleWin || '450',
              prize: game.winRate || '0',
              percentage: game.commissionRate || '0',
              gameType: gameType || 'Other',
              description: game.description || '',
              price: formatGamePrice(game.winRate, game.commissionRate),
            })),
          };
        });

        // Add an "All" tab at the start
        tabsData.unshift({
          title: 'All',
          cards: tabsData.flatMap(tab => tab.cards),
        });

        // Store games in session storage like in GameCategories
        sessionStorage.setItem('user_games', JSON.stringify(tabsData.flatMap(tab => tab.cards)));

        setTabsData(tabsData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching games:', error);
        setLoading(false);
        setHasGames(false);
      }
    };

    if (tokens?.access?.token) {
      fetchGames();
    }
  }, [tokens]);

  // No games message
  const NoGamesMessage = () => (
    <div className='flex flex-col items-center justify-center h-[calc(100vh-150px)] px-4 text-center'>
      <div className='bg-blue-50 p-6 rounded-lg shadow-sm max-w-md'>
        <h3 className='text-xl font-semibold text-[#1E429A] mb-3'>
          No Games Available
        </h3>
        <p className='text-gray-600 mb-4'>
          You don't have any games available to play. Please contact your
          administrator to get games assigned to your account.
        </p>
        <Link
          to='/home'
          className='text-white bg-[#1E429A] px-4 py-2 rounded-md inline-block'
        >
          Return to Home
        </Link>
      </div>
    </div>
  );

  return (
    <div className='min-h-screen relative overflow-hidden'>
      <HeaderWithIcon title='Play Games' />

      {loading ? (
        <div className='flex justify-center items-center h-[calc(100vh-100px)]'>
          <img
            src={LoadingPng}
            alt='Loading'
            className='w-16 h-16 animate-spin'
          />
        </div>
      ) : !hasGames ? (
        <NoGamesMessage />
      ) : (
        <GameTabs tabsData={tabsData} />
      )}
    </div>
  );
}
